import { useEffect, useMemo, useState } from "react";
import DSBox from "views/system-design/DSBox";
import DSButton from "views/system-design/DSButton";
import DSChip from "views/system-design/DSChip";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSSelect from "views/system-design/DSSelect";
import DSTextField from "views/system-design/DSTextField";
import DSTooltip from "views/system-design/DSTooltip";
import DSTypography from "views/system-design/DSTypography";
import DSTable from "views/system-design/Table/DSTable";
import DSTableBody from "views/system-design/Table/DSTableBody";
import DSTableCell from "views/system-design/Table/DSTableCell";
import DSTableContainer from "views/system-design/Table/DSTableContainer";
import DSTableHead from "views/system-design/Table/DSTableHead";
import DSTableRow from "views/system-design/Table/DSTableRow";
import {
  SentryIssue,
  SentryIssueSort,
  STATS_PERIODS,
  StatsPeriod,
} from "../../../api/sentryIssues";
import {
  useSentryIssues,
  useSentryProjects,
  useSentryTaggedTickets,
  useSetSentryIssueStatus,
} from "../hooks/useSentryIssues";
import { openTicket } from "../sentryTicket";
import {
  ALL_DOMAINS,
  SentryStatusFilter,
  useLocalSentryFilters,
} from "../hooks/useLocalSentryFilters";
import { isNewIssue, isRegression } from "../hooks/useNewSentryIssues";
import SentryIssueDrawer from "./SentryIssueDrawer";
import IssueSparkline from "./IssueSparkline";
import {
  DOMAIN_SOURCE_LABELS,
  REGRESSION_COLOR,
  absoluteTime,
  domainColor,
  formatCount,
  levelColor,
  relativeTime,
} from "./domainStyles";

const STATUS_QUERIES: Record<SentryStatusFilter, string> = {
  unresolved: "is:unresolved",
  resolved: "is:resolved",
  ignored: "is:ignored",
  all: "",
};

const SORT_LABELS: Record<SentryIssueSort, string> = {
  [SentryIssueSort.LastSeen]: "Last seen",
  [SentryIssueSort.FirstSeen]: "First seen",
  [SentryIssueSort.Events]: "Events",
  [SentryIssueSort.Users]: "Users",
};

const PAGE_SIZE = 100;
const COLUMN_COUNT = 8;

const SentryIssues = () => {
  const { filters, setFilter, resetFilters, isDefault } =
    useLocalSentryFilters();
  // The search box holds an unsubmitted draft; only Enter commits it to the
  // stored filters (and therefore to a request).
  const [searchDraft, setSearchDraft] = useState(filters.search);
  useEffect(() => setSearchDraft(filters.search), [filters.search]);

  // Sentry paginates by opaque cursor, so walking back means remembering the
  // cursors already visited rather than computing an offset.
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const cursor = cursorStack[cursorStack.length - 1];
  const resetPaging = () => setCursorStack([undefined]);

  /** Every filter change invalidates the cursor: page 2 of the old filter is meaningless. */
  const changeFilter = <K extends keyof typeof filters>(
    field: K,
    value: (typeof filters)[K],
  ) => {
    setFilter(field, value);
    if (field !== "domain") resetPaging();
  };

  const query = [STATUS_QUERIES[filters.status], filters.search]
    .filter(Boolean)
    .join(" ");

  const { data: sentryProjects } = useSentryProjects();
  const { data, isLoading, isFetching, isError, error, refetch } =
    useSentryIssues({
      projects: filters.projects,
      query,
      statsPeriod: filters.statsPeriod,
      sort: filters.sort,
      environment: filters.environment || undefined,
      limit: PAGE_SIZE,
      cursor,
    });

  const setStatusMutation = useSetSentryIssueStatus();
  const { data: ticketsByPermalink } = useSentryTaggedTickets();
  const [selected, setSelected] = useState<SentryIssue | null>(null);

  const issues = data?.issues ?? [];

  // Domain is derived by us rather than stored in Sentry, so it cannot be part
  // of the Sentry query — the counts and the filter both work on the page.
  const domainCounts = useMemo(() => {
    const counts = new Map<string, number>();
    issues.forEach((issue) =>
      counts.set(issue.domain, (counts.get(issue.domain) ?? 0) + 1),
    );
    return Array.from(counts.entries()).sort(
      (first, second) => second[1] - first[1],
    );
  }, [issues]);

  const visibleIssues =
    filters.domain === ALL_DOMAINS
      ? issues
      : issues.filter((issue) => issue.domain === filters.domain);

  return (
    <DSBox>
      <DSBox
        sx={{
          display: "flex",
          gap: 1.5,
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
        }}
      >
        <DSSelect
          multiple
          displayEmpty
          size="small"
          value={filters.projects}
          onChange={(event) =>
            changeFilter("projects", event.target.value as string[])
          }
          renderValue={(value) =>
            (value as string[]).length
              ? (value as string[]).join(", ")
              : "All projects"
          }
          sx={{ minWidth: 190 }}
        >
          {sentryProjects?.map((project) => (
            <DSMenuItem key={project.slug} value={project.slug}>
              {project.slug}
            </DSMenuItem>
          ))}
        </DSSelect>

        <DSSelect
          size="small"
          value={filters.status}
          onChange={(event) =>
            changeFilter("status", event.target.value as SentryStatusFilter)
          }
          sx={{ minWidth: 130 }}
        >
          {Object.keys(STATUS_QUERIES).map((key) => (
            <DSMenuItem key={key} value={key}>
              {key}
            </DSMenuItem>
          ))}
        </DSSelect>

        <DSSelect
          size="small"
          value={filters.statsPeriod}
          onChange={(event) =>
            changeFilter("statsPeriod", event.target.value as StatsPeriod)
          }
          sx={{ minWidth: 100 }}
        >
          {STATS_PERIODS.map((period) => (
            <DSMenuItem key={period} value={period}>
              Last {period}
            </DSMenuItem>
          ))}
        </DSSelect>

        <DSSelect
          size="small"
          value={filters.sort}
          onChange={(event) =>
            changeFilter("sort", event.target.value as SentryIssueSort)
          }
          sx={{ minWidth: 130 }}
        >
          {Object.entries(SORT_LABELS).map(([value, label]) => (
            <DSMenuItem key={value} value={value}>
              {label}
            </DSMenuItem>
          ))}
        </DSSelect>

        <DSSelect
          size="small"
          displayEmpty
          value={filters.environment}
          onChange={(event) =>
            changeFilter("environment", event.target.value as string)
          }
          sx={{ minWidth: 150 }}
        >
          <DSMenuItem value="">All environments</DSMenuItem>
          <DSMenuItem value="production">production</DSMenuItem>
          <DSMenuItem value="development">development</DSMenuItem>
        </DSSelect>

        <DSTextField
          size="small"
          placeholder="Sentry search, e.g. TypeError"
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            changeFilter("search", searchDraft.trim());
          }}
          sx={{ minWidth: 230 }}
        />

        <DSBox
          sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}
        >
          {isFetching && <DSCircularProgress size={18} />}
          <DSTooltip
            title={
              isDefault
                ? "Filters are already at their defaults"
                : "Back to all projects, unresolved, last 24h"
            }
          >
            {/* A disabled MUI button swallows hover, so the tooltip needs a
                wrapper element to attach to. */}
            <span>
              <DSButton
                variant="outlined"
                color="secondary"
                size="small"
                disabled={isDefault}
                onClick={() => {
                  resetFilters();
                  resetPaging();
                }}
              >
                Reset filters
              </DSButton>
            </span>
          </DSTooltip>
          <DSButton
            variant="outlined"
            color="secondary"
            size="small"
            disabled={isFetching}
            onClick={() => refetch()}
          >
            Refresh
          </DSButton>
        </DSBox>
      </DSBox>

      {domainCounts.length > 0 && (
        <DSBox sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
          <DSChip
            label={`All (${issues.length})`}
            size="small"
            variant={filters.domain === ALL_DOMAINS ? "filled" : "outlined"}
            onClick={() => setFilter("domain", ALL_DOMAINS)}
          />
          {domainCounts.map(([domainName, count]) => (
            <DSChip
              key={domainName}
              label={`${domainName} (${count})`}
              size="small"
              variant={filters.domain === domainName ? "filled" : "outlined"}
              onClick={() => setFilter("domain", domainName)}
              sx={{
                borderColor: domainColor(domainName),
                color:
                  filters.domain === domainName
                    ? "#fff"
                    : domainColor(domainName),
                bgcolor:
                  filters.domain === domainName
                    ? domainColor(domainName)
                    : undefined,
              }}
            />
          ))}
        </DSBox>
      )}

      {isError && (
        <DSTypography color="error" sx={{ mb: 2 }}>
          {error instanceof Error
            ? error.message
            : "Failed to load Sentry issues"}
        </DSTypography>
      )}

      {isLoading ? (
        <DSBox sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <DSCircularProgress />
        </DSBox>
      ) : (
        <DSTableContainer>
          <DSTable size="small">
            <DSTableHead>
              <DSTableRow>
                <DSTableCell>Issue</DSTableCell>
                <DSTableCell>Domain</DSTableCell>
                <DSTableCell>Project</DSTableCell>
                <DSTableCell>Events</DSTableCell>
                <DSTableCell>Users</DSTableCell>
                <DSTableCell>First seen</DSTableCell>
                <DSTableCell>Last seen</DSTableCell>
                <DSTableCell align="right">Actions</DSTableCell>
              </DSTableRow>
            </DSTableHead>
            <DSTableBody>
              {visibleIssues.map((issue) => (
                <DSTableRow
                  key={issue.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelected(issue)}
                >
                  <DSTableCell sx={{ maxWidth: 460 }}>
                    <DSBox
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <DSBox
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: levelColor(issue.level),
                          flexShrink: 0,
                        }}
                      />
                      <DSTypography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {issue.title}
                      </DSTypography>
                    </DSBox>
                    <DSBox
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {ticketsByPermalink
                        ?.get(issue.permalink)
                        ?.map((ticket) => (
                          <DSTooltip
                            key={ticket.id}
                            title={`${ticket.title} (${ticket.status})`}
                          >
                            <DSChip
                              label={ticket.key}
                              size="small"
                              variant="outlined"
                              onClick={(event) => {
                                // The row opens the drawer; the chip opens the ticket.
                                event.stopPropagation();
                                openTicket(ticket.id);
                              }}
                              sx={{
                                height: 18,
                                fontSize: 10,
                                flexShrink: 0,
                                opacity: ticket.status === "done" ? 0.55 : 1,
                              }}
                            />
                          </DSTooltip>
                        ))}
                      <DSTypography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          fontFamily: "monospace",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {issue.shortId} · {issue.culprit ?? "—"}
                      </DSTypography>
                    </DSBox>
                  </DSTableCell>

                  <DSTableCell>
                    <DSTooltip title={DOMAIN_SOURCE_LABELS[issue.domainSource]}>
                      <DSChip
                        label={issue.domain}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: domainColor(issue.domain),
                          color: domainColor(issue.domain),
                          // A domain that is only the project's own area is a
                          // weak claim; dim it so the strong ones stand out.
                          opacity:
                            issue.domainSource === "project" ||
                            issue.domainSource === "unknown"
                              ? 0.55
                              : 1,
                        }}
                      />
                    </DSTooltip>
                  </DSTableCell>

                  <DSTableCell>
                    <DSTypography variant="caption">
                      {issue.projectSlug}
                    </DSTypography>
                  </DSTableCell>

                  <DSTableCell>
                    <DSBox
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <IssueSparkline
                        counts={issue.eventCounts}
                        color={levelColor(issue.level)}
                      />
                      <DSTooltip
                        title={`${issue.lifetimeCount.toLocaleString()} events all time`}
                      >
                        <DSTypography variant="body2">
                          {formatCount(issue.count)}
                        </DSTypography>
                      </DSTooltip>
                    </DSBox>
                  </DSTableCell>

                  <DSTableCell>
                    <DSTypography variant="body2">
                      {formatCount(issue.userCount)}
                    </DSTypography>
                  </DSTableCell>

                  <DSTableCell>
                    <DSTooltip title={absoluteTime(issue.firstSeen)}>
                      <DSTypography variant="caption">
                        {relativeTime(issue.firstSeen)}
                      </DSTypography>
                    </DSTooltip>
                    {/* A regression is the one thing "first seen" hides: the
                        issue is old, but it came back after being resolved. */}
                    {isRegression(issue) && (
                      <DSTooltip title="Resolved once, then started erroring again">
                        <DSChip
                          label="regressed"
                          size="small"
                          sx={{
                            display: "flex",
                            width: "fit-content",
                            mt: 0.25,
                            height: 18,
                            fontSize: 10,
                            bgcolor: REGRESSION_COLOR,
                            color: "#fff",
                          }}
                        />
                      </DSTooltip>
                    )}
                    {isNewIssue(issue) && (
                      <DSChip
                        label="new"
                        size="small"
                        variant="outlined"
                        sx={{
                          display: "flex",
                          width: "fit-content",
                          mt: 0.25,
                          height: 18,
                          fontSize: 10,
                        }}
                      />
                    )}
                  </DSTableCell>

                  <DSTableCell>
                    <DSTooltip title={absoluteTime(issue.lastSeen)}>
                      <DSTypography variant="caption">
                        {relativeTime(issue.lastSeen)}
                      </DSTypography>
                    </DSTooltip>
                  </DSTableCell>

                  <DSTableCell align="right">
                    <DSBox
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "flex-end",
                      }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <DSButton
                        size="small"
                        color="secondary"
                        onClick={() =>
                          window.open(issue.permalink, "_blank", "noreferrer")
                        }
                      >
                        Sentry
                      </DSButton>
                      {issue.status === "unresolved" ? (
                        <DSButton
                          size="small"
                          variant="outlined"
                          color="secondary"
                          disabled={setStatusMutation.isPending}
                          onClick={() =>
                            setStatusMutation.mutate({
                              issueId: issue.id,
                              action: "resolve",
                            })
                          }
                        >
                          Resolve
                        </DSButton>
                      ) : (
                        <DSChip
                          label={issue.status}
                          size="small"
                          color="success"
                        />
                      )}
                    </DSBox>
                  </DSTableCell>
                </DSTableRow>
              ))}

              {visibleIssues.length === 0 && (
                <DSTableRow>
                  <DSTableCell colSpan={COLUMN_COUNT}>
                    <DSTypography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2, textAlign: "center" }}
                    >
                      No issues match these filters.
                    </DSTypography>
                  </DSTableCell>
                </DSTableRow>
              )}
            </DSTableBody>
          </DSTable>
        </DSTableContainer>
      )}

      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <DSTypography variant="caption" color="text.secondary">
          {filters.domain === ALL_DOMAINS
            ? `${issues.length} issue${issues.length === 1 ? "" : "s"} on this page`
            : `${visibleIssues.length} of ${issues.length} on this page (filtered by domain)`}
        </DSTypography>
        <DSBox sx={{ display: "flex", gap: 1 }}>
          <DSButton
            size="small"
            variant="outlined"
            color="secondary"
            disabled={cursorStack.length === 1 || isFetching}
            onClick={() => setCursorStack((stack) => stack.slice(0, -1))}
          >
            Previous
          </DSButton>
          <DSButton
            size="small"
            variant="outlined"
            color="secondary"
            disabled={!data?.nextCursor || isFetching}
            onClick={() =>
              setCursorStack((stack) => [
                ...stack,
                data?.nextCursor ?? undefined,
              ])
            }
          >
            Next
          </DSButton>
        </DSBox>
      </DSBox>

      <SentryIssueDrawer issue={selected} onClose={() => setSelected(null)} />
    </DSBox>
  );
};

export default SentryIssues;
